/**
 * Quick replies fill a session's message box (ADR 0056): the card offers the choices a message
 * lists, and a click puts one in whichever box answers that session (the session-control box or
 * a headless session's composer), focused and not sent.
 */
import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';

type Fill = (text: string) => void;
const boxes = new Map<string, Set<Fill>>();

/** The id of a session's message box, so a quick reply can focus it. */
export const replyBoxId = (sessionId: string) => `reply-${sessionId}`;

/** Put `text` in the session's message box; false when no box for it is on screen. */
export function fillReply(sessionId: string, text: string): boolean {
  const set = boxes.get(sessionId);
  if (!set?.size) return false;
  // Render the filled box now (no frame timer), then put the cursor at its end.
  flushSync(() => {
    for (const f of set) f(text);
  });
  const el = document.getElementById(replyBoxId(sessionId));
  if (el instanceof HTMLTextAreaElement) {
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollIntoView({ block: 'nearest' });
  }
  return true;
}

/** Focus the session's message box where it is (draft kept); false when none is on screen. */
export function focusReply(sessionId: string): boolean {
  const el = document.getElementById(replyBoxId(sessionId));
  if (!(el instanceof HTMLTextAreaElement)) return false;
  el.scrollIntoView({ block: 'center' });
  el.focus({ preventScroll: true });
  return true;
}

/** Mount in a message box: `onFill` receives a quick reply for its session. */
export function useReplyFill(sessionId: string, onFill: Fill): void {
  const ref = useRef(onFill);
  useEffect(() => {
    ref.current = onFill;
  });
  useEffect(() => {
    const f: Fill = (t) => ref.current(t);
    const set = boxes.get(sessionId) ?? new Set<Fill>();
    boxes.set(sessionId, set);
    set.add(f);
    return () => {
      set.delete(f);
      if (!set.size) boxes.delete(sessionId);
    };
  }, [sessionId]);
}
